import * as faceapi from "face-api.js";
import { apiPostPut } from "../api/api_methods";
import { uploadFileToAzure } from "../services/azure";

let detectionIntervalId = null;
let mediaStream = null;
let audioContext = null;
let video = null;
let canvas = null;
let analyser = null;

// Global variables for reference face management
let knownDescriptors = [];
let faceMatcher = null;
let isReferenceFaceInitialized = false;
let referenceFaceCollectionMode = false;
let referenceFaceCollectionCount = 0;
const REFERENCE_FACE_COUNT = 10; // Collect more reference samples for better accuracy

export async function startAdvancedProxyDetection(storeInstance, interviewId, intervieweeId) {
  try {
    // Add device capability detection
    const deviceCapabilities = await detectDeviceCapabilities();
    console.log("Device capabilities:", deviceCapabilities);

    video = document.createElement("video");
    video.setAttribute("autoplay", "");
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.style.display = "none";
    document.body.appendChild(video);

    canvas = document.createElement("canvas");
    canvas.style.display = "none";
    document.body.appendChild(canvas);

    // Load models with better error handling
    await loadFaceApiModels();

    // Use adaptive video constraints based on device capabilities
    const videoConstraints = getAdaptiveVideoConstraints(deviceCapabilities);
    console.log("Using video constraints:", videoConstraints);

    mediaStream = await navigator.mediaDevices.getUserMedia({ 
      video: videoConstraints,
      audio: true 
    });
    
    const videoOnlyStream = new MediaStream(mediaStream.getVideoTracks());
    video.srcObject = videoOnlyStream;

    // Wait for video to be ready
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = reject;
      // Timeout after 10 seconds
      setTimeout(() => reject(new Error("Video loading timeout")), 10000);
    });

    console.log("Video stream ready:", {
      width: video.videoWidth,
      height: video.videoHeight,
      fps: mediaStream.getVideoTracks()[0]?.getSettings()?.frameRate
    });

    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 4096;
    analyser.smoothingTimeConstant = 0.8;
    const source = audioContext.createMediaStreamSource(mediaStream);
    source.connect(analyser);

    const proxyReport = {
      eventCounts: {
        multipleFaces: 0,
        multipleVoices: 0,
        headLeft: 0,
        headRight: 0,
        headUp: 0,
        eyeDown: 0,
      },
      proxyEvents: [],
    };

    const tabChangeEvents = [];
    const externalMonitorDetected = window.screen.width > window.screen.availWidth;

    let lastTabChange = 0;
    document.addEventListener("visibilitychange", () => {
      const now = Date.now();
      if (document.hidden && now - lastTabChange > 1000) {
        tabChangeEvents.push(new Date().toISOString());
        lastTabChange = now;
      }
    });

      // Reset reference face state for new session
  knownDescriptors = [];
  faceMatcher = null;
  isReferenceFaceInitialized = false;
  referenceFaceCollectionMode = false;
  referenceFaceCollectionCount = 0;

    async function captureImageProof(label) {
      return new Promise((resolve) => {
        try {
          const context = canvas.getContext("2d");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          context.save();
          context.scale(-1, 1);
          context.translate(-canvas.width, 0);
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          context.restore();

          canvas.toBlob(async (blob) => {
            if (!blob) return resolve(null);
            const response = await uploadFileToAzure(
              `proxy-proof-${Date.now()}.jpg`,
              blob,
              { action: "recording" },
              () => {}
            );
            resolve(response?.url || response?.body?.fileUrl || null);
          }, "image/jpeg");
        } catch (err) {
          console.error("Error capturing image proof:", err);
          resolve(null);
        }
      });
    }

    async function updateProxyReport(data, interviewId) {
      const response = await apiPostPut(data, `/new-proxy/update-proxy-by-interview-id/${interviewId}`, "POST");
      if (response.status === 200) return response?.body?.data;
      console.error("Failed to update proxy report:", response);
      return false;
    }

    function calculateHeadPose(landmarks) {
      const nose = landmarks.getNose()[3];
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const jaw = landmarks.getJawOutline()[8];
      const forehead = landmarks.getJawOutline()[0];

      const eyeMidX = (leftEye[0].x + rightEye[3].x) / 2;
      const eyeMidY = (leftEye[0].y + rightEye[3].y) / 2;
      const dx = nose.x - eyeMidX;
      const dy = nose.y - eyeMidY;

      const faceHeight = jaw.y - forehead.y;
      const eyeWidth = rightEye[3].x - leftEye[0].x;

      const yaw = (dx / eyeWidth) * 100;
      const pitch = (dy / faceHeight) * 100;

      return { pitch, yaw };
    }

    function detectEyeGaze(landmarks) {
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();

      if (!leftEye || !rightEye || leftEye.length < 6 || rightEye.length < 6) {
        return { vertical: "center" };
      }

      const leftEyeCenter = {
        x: leftEye.reduce((sum, p) => sum + p.x, 0) / leftEye.length,
        y: leftEye.reduce((sum, p) => sum + p.y, 0) / leftEye.length,
      };
      const rightEyeCenter = {
        x: rightEye.reduce((sum, p) => sum + p.x, 0) / rightEye.length,
        y: rightEye.reduce((sum, p) => sum + p.y, 0) / rightEye.length,
      };

      const leftUpperEyelid = (leftEye[1].y + leftEye[2].y) / 2;
      const leftLowerEyelid = (leftEye[4].y + leftEye[5].y) / 2;
      const leftEyelidMid = (leftUpperEyelid + leftLowerEyelid) / 2;

      const rightUpperEyelid = (rightEye[1].y + rightEye[2].y) / 2;
      const rightLowerEyelid = (rightEye[4].y + rightEye[5].y) / 2;
      const rightEyelidMid = (rightUpperEyelid + rightLowerEyelid) / 2;

      const leftDiff = leftEyeCenter.y - leftEyelidMid;
      const rightDiff = rightEyeCenter.y - rightEyelidMid;

      const avgDiff = (leftDiff + rightDiff) / 2;
      const eyeWidth = rightEye[3].x - leftEye[0].x;
      const normalizedDiff = avgDiff / eyeWidth;

      if (normalizedDiff > 0.05) return { vertical: "down" };
      return { vertical: "center" };
    }

    const isSimilarDescriptor = (desc, descriptors, threshold = 0.6) => {
      return descriptors.some(kd => {
        const dist = faceapi.euclideanDistance(desc, kd);
        return dist < threshold;
      });
    };

      const detectFaces = async () => {
    try {
      // Check if video is ready and has valid dimensions
      if (!video || !video.videoWidth || !video.videoHeight) {
        console.log("Video not ready for face detection");
        return;
      }

      // Use adaptive confidence based on device performance
      const confidenceThreshold = window.deviceCapabilities?.isMobile ? 0.4 : 0.5;
      
      const detections = await faceapi
        .detectAllFaces(video, new faceapi.SsdMobilenetv1Options({ minConfidence: confidenceThreshold }))
        .withFaceLandmarks()
        .withFaceDescriptors();

      if (detections.length === 0) {
        console.log("No faces detected");
        return;
      }

      console.log(`Detected ${detections.length} face(s)`);

        // Initialize reference face collection mode if not started
        if (!isReferenceFaceInitialized && !referenceFaceCollectionMode && detections.length === 1) {
          referenceFaceCollectionMode = true;
          console.log("Starting reference face collection...");
        }

        // Collect reference face descriptors
        if (referenceFaceCollectionMode && detections.length === 1 && referenceFaceCollectionCount < REFERENCE_FACE_COUNT) {
          knownDescriptors.push(detections[0].descriptor);
          referenceFaceCollectionCount++;
          console.log(`Reference face sample ${referenceFaceCollectionCount}/${REFERENCE_FACE_COUNT} collected`);
          
          if (referenceFaceCollectionCount >= REFERENCE_FACE_COUNT) {
            // Create face matcher with more lenient threshold
            faceMatcher = new faceapi.FaceMatcher(
              [new faceapi.LabeledFaceDescriptors("interviewee", knownDescriptors)],
              0.7 // More lenient threshold to reduce false positives
            );
            isReferenceFaceInitialized = true;
            referenceFaceCollectionMode = false;
            console.log("Reference face initialized with", REFERENCE_FACE_COUNT, "descriptors. Threshold: 0.7");
          }
          return; // Wait until all reference descriptors are collected
        }

        // Step 1: Check for multiple faces
        let distinctFaces = [];
        for (const det of detections) {
          if (!isSimilarDescriptor(det.descriptor, distinctFaces, 0.5)) {
            distinctFaces.push(det.descriptor);
          }
        }

        if (distinctFaces.length > 1) {
          proxyReport.eventCounts.multipleFaces += 1;
          const imageUrl = await captureImageProof("Multiple Faces Detected");
          const data = {
            anomalyType: "multipleFacesDetected",
            proof: {
              type: "image",
              label: "Multiple Faces Detected",
              description: `Multiple faces detected at ${new Date().toLocaleTimeString()}`,
              url: imageUrl,
            },
          };
          if (interviewId) await updateProxyReport(data, interviewId);
          proxyReport.proxyEvents.push({
            type: "Multiple Faces Detected",
            time: new Date().toLocaleTimeString(),
          });
          return; // Skip all other detections
        }

        // Step 2: Check for different person (single face only) - Only after reference is initialized
        if (detections.length === 1 && faceMatcher && isReferenceFaceInitialized) {
          const det = detections[0];
          const match = faceMatcher.findBestMatch(det.descriptor);
          
          console.log(`Face match: ${match.label}, distance: ${match.distance.toFixed(3)}`);
          
          // More lenient threshold and confirmation logic
          let isDifferentPerson = false;
          const DIFFERENT_PERSON_THRESHOLD = 0.8; // Much more lenient
          
          if (match.label !== "interviewee" || match.distance > DIFFERENT_PERSON_THRESHOLD) {
            // Perform multiple confirmation checks with delays
            let confirmationCount = 0;
            const maxConfirmations = 3;
            
            for (let i = 0; i < maxConfirmations; i++) {
              await new Promise(resolve => setTimeout(resolve, 500)); // Shorter delay
              const newDetections = await faceapi
                .detectAllFaces(video, new faceapi.SsdMobilenetv1Options({ minConfidence: confidenceThreshold }))
                .withFaceLandmarks()
                .withFaceDescriptors();
              
              if (newDetections.length === 1) {
                const newMatch = faceMatcher.findBestMatch(newDetections[0].descriptor);
                console.log(`Confirmation ${i + 1}: ${newMatch.label}, distance: ${newMatch.distance.toFixed(3)}`);
                
                if (newMatch.label !== "interviewee" || newMatch.distance > DIFFERENT_PERSON_THRESHOLD) {
                  confirmationCount++;
                }
              }
            }
            
            // Only flag as different person if majority of confirmations agree
            isDifferentPerson = confirmationCount >= Math.ceil(maxConfirmations / 2);
          }

          if (isDifferentPerson) {
            console.log("Different person confirmed after multiple checks");
            proxyReport.eventCounts.multipleFaces += 1;
            const imageUrl = await captureImageProof("Different Person Detected");
            const data = {
              anomalyType: "multipleFacesDetected",
              proof: {
                type: "image",
                label: "Different Person Detected",
                description: `Different person detected at ${new Date().toLocaleTimeString()}`,
                url: imageUrl,
              },
            };
            if (interviewId) await updateProxyReport(data, interviewId);
            proxyReport.proxyEvents.push({
              type: "Different Person Detected",
              time: new Date().toLocaleTimeString(),
            });
            return; // Skip head detection
          }
        }

        // Step 3: Head pose and eye gaze detection (only for single face matching reference)
        if (detections.length === 1 && faceMatcher && isReferenceFaceInitialized) {
          const det = detections[0];
          const match = faceMatcher.findBestMatch(det.descriptor);
          
          // More lenient threshold for head pose detection
          if (match.label === "interviewee" && match.distance <= 0.8) {
            const headPose = calculateHeadPose(det.landmarks);
            const gaze = detectEyeGaze(det.landmarks);

            let headDirection = "";
            const YAW_THRESHOLD = 25;
            const PITCH_UP_THRESHOLD = -30; // Increased for stricter upward detection

            if (headPose.yaw > YAW_THRESHOLD) headDirection = "right";
            else if (headPose.yaw < -YAW_THRESHOLD) headDirection = "left";
            else if (headPose.pitch < PITCH_UP_THRESHOLD) headDirection = "up";

            const now = Date.now();
            if (headDirection && now - lastDirectionTime > 3000) {
              proxyReport.eventCounts[`head${headDirection.charAt(0).toUpperCase() + headDirection.slice(1)}`]++;
              const imageUrl = await captureImageProof(`Head ${headDirection}`);
              const data = {
                anomalyType: "faceOutOfView",
                proof: {
                  type: "image",
                  label: `Head ${headDirection}`,
                  description: `Head turned ${headDirection} at ${new Date().toLocaleTimeString()}`,
                  url: imageUrl,
                },
              };
              if (interviewId) await updateProxyReport(data, interviewId);
              proxyReport.proxyEvents.push({
                type: `Head Moving ${headDirection}`,
                time: new Date().toLocaleTimeString(),
              });
              lastDirectionTime = now;
            }

            if (gaze.vertical === "down" && now - lastEyeDownTime > 2000) {
              proxyReport.eventCounts.eyeDown += 1;
              const imageUrl = await captureImageProof("Eye Gaze Down");
              const data = {
                anomalyType: "faceOutOfView",
                proof: {
                  type: "image",
                  label: "Eye Gaze Down Detected",
                  description: `Eye gaze down at ${new Date().toLocaleTimeString()}`,
                  url: imageUrl,
                },
              };
              if (interviewId) await updateProxyReport(data, interviewId);
              proxyReport.proxyEvents.push({
                type: "Eye Gaze Down",
                time: new Date().toLocaleTimeString(),
              });
              lastEyeDownTime = now;
            }
          }
        }
      } catch (err) {
        console.error("Face detection error:", err);
      }
    };

    let lastNoiseFlagTime = 0;
    let lastDirectionTime = 0;
    let lastEyeDownTime = 0;

    const detectAudio = async () => {
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);

      const frequencyData = new Float32Array(bufferLength);
      analyser.getFloatFrequencyData(frequencyData);

      // Analyze human voice frequency range (80-400 Hz, roughly bins 4-20 at 44.1kHz sample rate)
      const voiceBand = frequencyData.slice(4, 20);
      // Detect peaks with very low threshold and minimum separation
      const voicePeaks = [];
      const minPeakDistance = 2; // Reduced for more sensitivity
      let lastPeakIndex = -minPeakDistance;

      for (let i = 1; i < voiceBand.length - 1; i++) {
          if (
          voiceBand[i] > -80 && // Extremely low threshold for maximum sensitivity
          voiceBand[i] > voiceBand[i - 1] &&
          voiceBand[i] > voiceBand[i + 1] &&
          i - lastPeakIndex >= minPeakDistance
          ) {
          voicePeaks.push({ index: i, value: voiceBand[i] });
          lastPeakIndex = i;
          }
      }

      const isMultipleVoices = voicePeaks.length >= 2; // At least 2 peaks for multiple voices
      // Calculate RMS volume for high noise detection
      const volume = Math.sqrt(dataArray.reduce((sum, val) => sum + val ** 2, 0) / bufferLength) / 255;
      const isHighNoise = volume > 0.4; // Very low threshold for sensitivity

      const currentTime = Date.now();

      // Detailed debug logging
      console.log(`Audio Analysis: Peaks=${voicePeaks.length}, PeakValues=${voicePeaks.map(p => p.value.toFixed(2)).join(',')}, Volume=${volume.toFixed(3)}, MultipleVoices=${isMultipleVoices}, HighNoise=${isHighNoise}, FrequencyDataSample=${voiceBand.slice(0, 5).map(v => v.toFixed(2)).join(',')}`);

      if (isMultipleVoices && currentTime - lastNoiseFlagTime >= 2000) {
          proxyReport.eventCounts.multipleVoices += 1;
          const imageUrl = await captureImageProof("Multiple Voices Detected");
          const data = {
          anomalyType: "multipleVoicesDetected",
          proof: {
              type: "image",
              label: "Multiple Voices Detected",
              description: `Multiple voices detected at ${new Date().toLocaleTimeString()}`,
              url: imageUrl,
          },
          };
          if (interviewId) await updateProxyReport(data, interviewId);
          proxyReport.proxyEvents.push({
          type: "Multiple Voices",
          time: new Date().toLocaleTimeString(),
          });
          lastNoiseFlagTime = currentTime;
          console.log("Triggered Multiple Voices Detection");
      }

      if (isHighNoise && currentTime - lastNoiseFlagTime >= 2000) {
          proxyReport.eventCounts.multipleVoices += 1;
          const imageUrl = await captureImageProof("High Noise Detected");
          const data = {
          anomalyType: "multipleVoicesDetected",
          proof: {
              type: "image",
              label: "High Noise Detected",
              description: `High noise detected at ${new Date().toLocaleTimeString()}`,
              url: imageUrl,
          },
          };
          if (interviewId) await updateProxyReport(data, interviewId);
          proxyReport.proxyEvents.push({
          type: "High Noise",
          time: new Date().toLocaleTimeString(),
          });
          lastNoiseFlagTime = currentTime;
          console.log("Triggered High Noise Detection");
      }
      };

      // Store device capabilities globally for adaptive detection
  window.deviceCapabilities = deviceCapabilities;

  detectionIntervalId = setInterval(async () => {
    try {
      await detectFaces();
      // detectAudio();
      window.proxyDetectionResult = {
        ...proxyReport,
        tabChanges: tabChangeEvents.length,
        externalMonitorDetected,
        deviceCapabilities,
        videoInfo: {
          width: video?.videoWidth,
          height: video?.videoHeight,
          ready: !!(video?.videoWidth && video?.videoHeight)
        }
      };
    } catch (error) {
      console.error("Detection interval error:", error);
      // Don't stop the interval for single detection errors
    }
  }, 1000);
  } catch (error) {
    console.error("Failed to start proxy detection:", error);
    throw error;
  }
}

// Function to manually initialize reference face (call this at interview start)
export async function initializeReferenceFace() {
  if (!video || !video.videoWidth || !video.videoHeight) {
    throw new Error("Video not ready for reference face initialization");
  }

  console.log("Manually initializing reference face...");
  
  // Reset reference face state
  knownDescriptors = [];
  faceMatcher = null;
  isReferenceFaceInitialized = false;
  referenceFaceCollectionMode = true;
  referenceFaceCollectionCount = 0;

  // Wait for reference face collection to complete
  return new Promise((resolve, reject) => {
    const checkInterval = setInterval(async () => {
      if (isReferenceFaceInitialized) {
        clearInterval(checkInterval);
        console.log("Reference face initialization completed successfully");
        resolve(true);
      }
    }, 1000);

    // Timeout after 30 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
      reject(new Error("Reference face initialization timeout"));
    }, 30000);
  });
}

export function stopAdvancedProxyDetection() {
  if (detectionIntervalId) {
    clearInterval(detectionIntervalId);
    detectionIntervalId = null;
  }

  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop());
    mediaStream = null;
  }

  if (audioContext) {
    audioContext.close().catch(err => console.error("AudioContext close error:", err));
    audioContext = null;
  }

  const videoEl = document.querySelector("video[autoplay][muted][playsinline]");
  const canvasEl = document.querySelector("canvas");

  if (videoEl && videoEl.parentNode) videoEl.parentNode.removeChild(videoEl);
  if (canvasEl && canvasEl.parentNode) canvasEl.parentNode.removeChild(canvasEl);

  // Reset reference face state
  knownDescriptors = [];
  faceMatcher = null;
  isReferenceFaceInitialized = false;
  referenceFaceCollectionMode = false;
  referenceFaceCollectionCount = 0;

  console.log("Advanced Proxy Detection Stopped");

  if (window.proxyDetectionResult) {
    console.log("Final Proxy Detection Report:", window.proxyDetectionResult);
  }
}

// Add diagnostic function for troubleshooting
export function diagnoseProxyDetectionIssues() {
  const diagnostics = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    mediaDevices: !!navigator.mediaDevices,
    getUserMedia: !!navigator.mediaDevices?.getUserMedia,
    webGL: false,
    faceApi: !!window.faceapi,
    deviceCapabilities: window.deviceCapabilities || null,
    proxyDetectionResult: window.proxyDetectionResult || null,
    referenceFace: {
      initialized: isReferenceFaceInitialized,
      collectionMode: referenceFaceCollectionMode,
      samplesCollected: referenceFaceCollectionCount,
      totalSamples: REFERENCE_FACE_COUNT,
      hasMatcher: !!faceMatcher
    },
    videoElement: {
      exists: !!document.querySelector('video[autoplay][muted][playsinline]'),
      ready: false,
      dimensions: null
    }
  };

  // Check WebGL
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    diagnostics.webGL = !!gl;
  } catch (e) {
    diagnostics.webGL = false;
  }

  // Check video element
  const videoEl = document.querySelector('video[autoplay][muted][playsinline]');
  if (videoEl) {
    diagnostics.videoElement.ready = !!(videoEl.videoWidth && videoEl.videoHeight);
    diagnostics.videoElement.dimensions = {
      width: videoEl.videoWidth,
      height: videoEl.videoHeight
    };
  }

  console.log("Proxy Detection Diagnostics:", diagnostics);
  return diagnostics;
}

// Function to get current reference face status
export function getReferenceFaceStatus() {
  return {
    initialized: isReferenceFaceInitialized,
    collectionMode: referenceFaceCollectionMode,
    samplesCollected: referenceFaceCollectionCount,
    totalSamples: REFERENCE_FACE_COUNT,
    hasMatcher: !!faceMatcher,
    descriptorsCount: knownDescriptors.length
  };
}

async function detectDeviceCapabilities() {
  const capabilities = {
    isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    isFirefox: navigator.userAgent.includes('Firefox'),
    isChrome: /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor),
    hasWebGL: false,
    hasMediaDevices: !!navigator.mediaDevices,
    screenResolution: { width: 0, height: 0 }, // Will be set in detectDeviceCapabilities
    connection: navigator.connection ? {
      effectiveType: navigator.connection.effectiveType,
      downlink: navigator.connection.downlink
    } : null
  };

  // Check WebGL support
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    capabilities.hasWebGL = !!gl;
  } catch (e) {
    capabilities.hasWebGL = false;
  }

  // Check screen properties safely
  try {
    capabilities.screenResolution = { 
      width: window.screen?.width || 0, 
      height: window.screen?.height || 0 
    };
  } catch (e) {
    capabilities.screenResolution = { width: 0, height: 0 };
  }

  return capabilities;
}

async function loadFaceApiModels() {
  const models = [
    { name: 'tinyFaceDetector', net: faceapi.nets.tinyFaceDetector },
    { name: 'faceLandmark68Net', net: faceapi.nets.faceLandmark68Net },
    { name: 'faceRecognitionNet', net: faceapi.nets.faceRecognitionNet },
    { name: 'ssdMobilenetv1', net: faceapi.nets.ssdMobilenetv1 }
  ];

  for (const model of models) {
    try {
      console.log(`Loading ${model.name}...`);
      await model.net.loadFromUri("/models/");
      console.log(`${model.name} loaded successfully`);
    } catch (error) {
      console.error(`Failed to load ${model.name}:`, error);
      throw new Error(`Model loading failed: ${model.name}`);
    }
  }
}

function getAdaptiveVideoConstraints(deviceCapabilities) {
  // Base constraints that work on most devices
  let constraints = {
    width: { ideal: 640, min: 320, max: 1280 },
    height: { ideal: 480, min: 240, max: 720 },
    frameRate: { ideal: 15, min: 10, max: 30 }
  };

  // Adjust for mobile devices
  if (deviceCapabilities.isMobile) {
    constraints = {
      width: { ideal: 480, min: 320, max: 640 },
      height: { ideal: 360, min: 240, max: 480 },
      frameRate: { ideal: 10, min: 5, max: 15 }
    };
  }

  // Safari-specific adjustments
  if (deviceCapabilities.isSafari) {
    constraints = {
      width: { ideal: 640, min: 320 },
      height: { ideal: 480, min: 240 },
      frameRate: { ideal: 15, min: 10 }
    };
  }

  // Firefox-specific adjustments
  if (deviceCapabilities.isFirefox) {
    constraints = {
      width: { ideal: 640, min: 320, max: 1280 },
      height: { ideal: 480, min: 240, max: 720 },
      frameRate: { ideal: 15, min: 10, max: 30 }
    };
  }

  // If device has poor connection, reduce quality
  if (deviceCapabilities.connection && deviceCapabilities.connection.effectiveType === 'slow-2g') {
    constraints.width.ideal = 320;
    constraints.height.ideal = 240;
    constraints.frameRate.ideal = 10;
  }

  return constraints;
}